import { useState, useEffect, useCallback, useRef } from 'react';
import { format, parseISO, addDays, isSameDay, isBefore, startOfDay } from 'date-fns';
import { useBooking } from '../contexts/booking/BookingContext';
import { getAvailableTimeSlotsForStaff, preloadAdjacentDates, fetchWithRetry } from '../lib/supabaseService';
import { toZonedTime, format as formatTz, fromZonedTime } from 'date-fns-tz';
import debounce from 'lodash/debounce';
import { getTotalDuration } from '../utils/bookingUtils';

// Texas timezone constant
const TEXAS_TIMEZONE = 'America/Chicago';

// Type for time slot display
export type TimeSlotDisplay = {
  time: string; // HH:mm:ss format 
  available: boolean;
};

/**
 * Custom hook for managing date and time selection in the booking process
 */
export function useDateTimeSelection() {
  const { bookingState, setSelectedDate, setSelectedTime } = useBooking();
  const { selectedServices, selectedStaff, selectedDate: currentSelectedDate, selectedTime: selectedTimeISO, editingAppointmentId } = bookingState;

  // Generate a 30-day date range
  const [availableDates, setAvailableDates] = useState<Date[]>([]);
  const [timeSlotsForDisplay, setTimeSlotsForDisplay] = useState<TimeSlotDisplay[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track if we've initialized the date from localStorage
  const hasInitializedDate = useRef(false);

  // Track previous date and staff to detect what changed (for edit mode time preservation)
  const prevDateRef = useRef<Date | null | undefined>(undefined);
  const prevStaffIdRef = useRef<string | null | undefined>(undefined);

  // Generate available dates (next 30 days)
  useEffect(() => {
    const today = new Date();
    const dates = Array.from({ length: 30 }, (_, i) => addDays(today, i));
    setAvailableDates(dates);
  }, []);

  // Initialize date on first mount - check localStorage for preferred date, otherwise use tomorrow
  useEffect(() => {
    // Only run once on mount
    if (hasInitializedDate.current) return;

    const today = new Date();
    const tomorrow = addDays(today, 1);

    // Check if there's a preferred date from homepage quick booking
    const bookingPreferences = localStorage.getItem('bookingPreferences');
    if (bookingPreferences) {
      try {
        const preferences = JSON.parse(bookingPreferences);
        if (preferences.preferredDate) {
          // Parse date string as local date (not UTC)
          // Input format is YYYY-MM-DD from date input
          const dateString = preferences.preferredDate;
          const [year, month, day] = dateString.split('-').map(Number);
          const preferredDate = new Date(year, month - 1, day); // month is 0-indexed

          // Only use preferred date if it's not in the past
          if (!isBefore(startOfDay(preferredDate), startOfDay(today))) {
            setSelectedDate(preferredDate);
            hasInitializedDate.current = true;
            // Clear the preference so it doesn't persist on refresh
            localStorage.removeItem('bookingPreferences');
            return;
          }
        }
      } catch (err) {
        console.error('Error parsing booking preferences:', err);
      }
    }

    // Default to tomorrow if no valid preferred date
    setSelectedDate(tomorrow);
    hasInitializedDate.current = true;
  }, [setSelectedDate]);

  // Handle case where currentSelectedDate becomes invalid (in the past)
  useEffect(() => {
    if (!hasInitializedDate.current) return;

    const today = new Date();
    if (currentSelectedDate && isBefore(startOfDay(currentSelectedDate), startOfDay(today))) {
      const tomorrow = addDays(today, 1);
      setSelectedDate(tomorrow);
    }
  }, [currentSelectedDate, setSelectedDate]);

  // Debounced function to fetch time slots
  const debouncedFetchSlots = useCallback(
    debounce(async (date: Date, staffId: string, duration: number) => {
      if (!date || !staffId || duration <= 0) return;

      setIsLoading(true);
      setError(null);

      try {
        // Use fetchWithRetry for resilience - automatically retries failed requests
        const availableSlotsFromBackend = await fetchWithRetry(
          () => getAvailableTimeSlotsForStaff(staffId, date, duration),
          3, // 3 retries maximum
          1000 // Start with 1 second delay, increases with each retry
        );

        // Define all possible time slots for display
        const ALL_POSSIBLE_TIME_SLOTS = [
          '09:00:00', '09:15:00', '09:30:00', '09:45:00',
          '10:00:00', '10:15:00', '10:30:00', '10:45:00',
          '11:00:00', '11:15:00', '11:30:00', '11:45:00',
          '12:00:00', '12:15:00', '12:30:00', '12:45:00',
          '13:00:00', '13:15:00', '13:30:00', '13:45:00',
          '14:00:00', '14:15:00', '14:30:00', '14:45:00',
          '15:00:00', '15:15:00', '15:30:00', '15:45:00',
          '16:00:00', '16:15:00', '16:30:00', '16:45:00',
          '17:00:00', '17:15:00', '17:30:00', '17:45:00',
          '18:00:00', '18:15:00', '18:30:00', '18:45:00'
        ];

        const displaySlots = ALL_POSSIBLE_TIME_SLOTS.map(timeStr => ({
          time: timeStr,
          available: availableSlotsFromBackend.includes(timeStr)
        }));

        setTimeSlotsForDisplay(displaySlots);
        
        if (availableSlotsFromBackend.length === 0 && displaySlots.length > 0) {
          setError('No available slots for this staff member on this date with the selected services. Please try a different date, staff member, or services.');
        }

        // Preload adjacent dates in the background
        preloadAdjacentDates(staffId, date, duration);
      } catch (err: any) {
        console.error('[useDateTimeSelection] Error fetching available time slots:', err);
        // Provide more specific error messages based on error type
        if (err.message && err.message.includes('network')) {
          setError('Network error. Please check your internet connection and try again.');
        } else if (err.message && err.message.includes('timeout')) {
          setError('Request timed out. The server is taking too long to respond. Please try again later.');
        } else if (err.code && err.code === 'PGRST301') {
          setError('There was a database error. Please try again or contact support if the problem persists.');
        } else {
          setError('Failed to load time slots due to an error. Please try again.');
        }
        setTimeSlotsForDisplay([]);
      } finally {
        setIsLoading(false);
      }
    }, 300),
    []
  );

  // Fetch time slots when dependencies change
  useEffect(() => {
    // Determine what changed to decide whether to clear the selected time
    const prevDate = prevDateRef.current;
    const prevStaffId = prevStaffIdRef.current;
    const currentStaffId = selectedStaff?.id ?? null;

    prevDateRef.current = currentSelectedDate;
    prevStaffIdRef.current = currentStaffId;

    const isFirstRun = prevDate === undefined;
    const dateChanged = !isFirstRun && !(prevDate && currentSelectedDate && isSameDay(prevDate, currentSelectedDate)) && prevDate !== currentSelectedDate;
    const staffChanged = !isFirstRun && prevStaffId !== currentStaffId;

    // In edit mode, preserve the selected time if only services changed (not date or staff).
    // On first run in edit mode, initEditMode already set the time so don't clear it.
    // In create mode or when date/staff changed, always clear.
    if (!isFirstRun && (!editingAppointmentId || dateChanged || staffChanged)) {
      setSelectedTime(null);
    }
    setError(null);

    if (!currentSelectedDate) {
      setError('Please select a date first.');
      return;
    }
    if (!selectedStaff) {
      setError('Please select a staff member first.');
      return;
    }
    if (selectedServices.length === 0) {
      setError('Please select services first.');
      return;
    }

    const currentTotalDuration = getTotalDuration(selectedServices);

    if (currentTotalDuration <= 0) {
      console.warn("[useDateTimeSelection] Total duration is 0 or invalid.");
      setError('Cannot determine service duration. Please check selected services.');
      return;
    }

    // Trigger the debounced fetch
    debouncedFetchSlots(currentSelectedDate, selectedStaff.id, currentTotalDuration);

    // Clean up function to cancel any pending debounced calls
    return () => {
      debouncedFetchSlots.cancel();
    };
  }, [currentSelectedDate, selectedStaff, selectedServices, setSelectedTime, debouncedFetchSlots, editingAppointmentId]);

  // Handle date selection
  const handleDateSelection = useCallback((date: Date) => {
    setSelectedDate(date);
    // Clear selected time when date changes
    setSelectedTime(null);
  }, [setSelectedDate, setSelectedTime]);
  
  // Handle time selection
  const handleTimeSelection = useCallback((timeToSelect: string) => {
    if (!currentSelectedDate) return;
    
    // Check if the slot is available
    const slot = timeSlotsForDisplay.find(s => s.time === timeToSelect);
    if (!slot || !slot.available) {
      return;
    }
    
    try {
      // Construct the full date and time string
      const dateStr = format(currentSelectedDate, 'yyyy-MM-dd');
      const dateTimeStr = `${dateStr}T${timeToSelect}`;
      
      // Parse as local time first
      const localDateTime = parseISO(dateTimeStr);
      
      // Now convert FROM Texas timezone TO UTC for storage
      // This treats localDateTime as if it's in Texas time and converts to UTC
      const utcForStorage = fromZonedTime(localDateTime, TEXAS_TIMEZONE);
      
      // Set the selected time in the context (keep the 'Z' for now, will be stripped when saving)
      setSelectedTime(utcForStorage.toISOString());
    } catch (err) {
      console.error('Error handling time selection:', err);
    }
  }, [currentSelectedDate, timeSlotsForDisplay, setSelectedTime]);
  
  // Format selected time for display and comparison
  const getSelectedTimeFormatted = useCallback(() => {
    if (!selectedTimeISO) return null;
    
    try {
      const parsedDate = parseISO(selectedTimeISO);
      const texasTime = toZonedTime(parsedDate, TEXAS_TIMEZONE);
      return formatTz(texasTime, 'HH:mm:ss', { timeZone: TEXAS_TIMEZONE });
    } catch (err) {
      console.error('Error formatting selected time:', err);
      return null;
    }
  }, [selectedTimeISO]);
  
  // Format time for display (12-hour format with AM/PM)
  const formatTimeForDisplay = useCallback((timeStr: string) => {
    if (!currentSelectedDate) return timeStr;
    
    try {
      const dateTimeStr = `${format(currentSelectedDate, 'yyyy-MM-dd')}T${timeStr}`;
      const parsedDateTime = parseISO(dateTimeStr);
      return format(parsedDateTime, 'h:mm a');
    } catch (err) {
      console.error('Error formatting time for display:', err);
      return timeStr;
    }
  }, [currentSelectedDate]);

  return {
    availableDates,
    timeSlotsForDisplay,
    isLoading,
    error,
    selectedTimeFormatted: getSelectedTimeFormatted(),
    handleDateSelection,
    handleTimeSelection,
    formatTimeForDisplay,
    currentSelectedDate
  };
}
